import React, { Ref, useImperativeHandle, useMemo, useState } from "react";

import { useSoilSightCurrency, useTranslation } from "utils";

import { CHART_COLORS, COMPARE_TABLE_SCROLL_Y, EditableFieldKey } from "consts";

import type { SoilSightCropDefaultsByCropId, SoilSightNonCropDefaults } from "models";

import { Button, Flex, Icon, KlimRadioGroup, SoilSightLoader, Typography } from "components";

import { ScenarioPills } from "pages/app/scenario-analysis/components/ScenarioPills";
import { StaleOverlay } from "pages/app/scenario-analysis/components/StaleOverlay";
import { CompareTable } from "pages/app/scenario-analysis/components/detailed-calculations/CompareTable";
import { DetailBreakdownTable } from "pages/app/scenario-analysis/components/detailed-calculations/DetailBreakdownTable";
import { DiscardChangesModal } from "pages/app/scenario-analysis/components/detailed-calculations/DiscardChangesModal";
import { EditTable } from "pages/app/scenario-analysis/components/detailed-calculations/EditTable";
import { DetailedCalcsLeaveGuard, DetailedCalcsView } from "pages/app/scenario-analysis/hooks/useScenarioAnalysisTabs";
import { useScenarioCalculationEntries } from "pages/app/scenario-analysis/hooks/useScenarioCalculationEntries";
import { CropYear, ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import {
  buildCoverCropCostByYearId,
  buildCropLabelResolver,
  buildFieldFallbackResolver,
  effectiveFieldValue,
  haveEditableFieldsChanged,
} from "pages/app/scenario-analysis/utils/detailedCalculationsEdit";
import { SoilSightOutcomesByScenario, scenarioLineColor } from "pages/app/scenario-analysis/utils/forecast";
import { tourTargetProps } from "pages/app/scenario-analysis/utils/outcomesTourTarget";

const PREFIX = "detailedCalculations.";

export interface DetailedCalculationsTabProps {
  scenarios: ScenarioData[];
  soilSightOutcomes: SoilSightOutcomesByScenario | null;
  mainCropLabelLookup: Record<string, string>;
  cropDefaultsByCropId?: SoilSightCropDefaultsByCropId;
  coverCropDefaults?: SoilSightNonCropDefaults["coverCropDefaults"] | null;
  scenarioId: string | null;
  onSelectScenario: (scenarioId: string) => void;
  /** Compare applies only with two or more scenarios and never while editing; otherwise Details shows. */
  view?: DetailedCalcsView;
  onViewChange?: (view: DetailedCalcsView) => void;
  isEditing: boolean;
  onEditingChange: (isEditing: boolean) => void;
  /** Writes one field straight through; there is no draft to commit on Done. */
  onFieldChange: (scenarioId: string, yearId: string, field: EditableFieldKey, value: number | null) => void;
  /** Puts back the values a scenario had when editing began; resolves true when the figures are current again. */
  onDiscardEdits?: (scenarioId: string, snapshot: CropYear[]) => boolean;
  /** Filled with a guard the page asks before leaving the tab mid-edit. */
  leaveGuardRef?: Ref<DetailedCalcsLeaveGuard>;
  isRecalculating?: boolean;
  isStale?: boolean;
  onRecalculate?: () => void;
  needsRotationFix?: boolean;
  onFixRotation?: () => void;
  dataTestId?: string;
}

/** The year-by-year derivation as a tab. */
export const DetailedCalculationsTab = ({
  scenarios,
  soilSightOutcomes,
  mainCropLabelLookup,
  cropDefaultsByCropId,
  coverCropDefaults,
  scenarioId,
  onSelectScenario,
  view = "detail",
  onViewChange,
  isEditing,
  onEditingChange,
  onFieldChange,
  onDiscardEdits,
  leaveGuardRef,
  isRecalculating = false,
  isStale = false,
  onRecalculate,
  needsRotationFix = false,
  onFixRotation,
  dataTestId = "detailed-calculations-tab",
}: DetailedCalculationsTabProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { currencySymbol } = useSoilSightCurrency();
  const [otherExpanded, setOtherExpanded] = useState(false);

  const { entries } = useScenarioCalculationEntries(scenarios, soilSightOutcomes);
  // The baseline anchors the delta column; with no baseline the first entry stands in.
  const baselineEntry = useMemo(() => entries.find((entry) => entry.scenario.isBaseline) ?? entries[0], [entries]);
  const hasMultipleScenarios = entries.length > 1;
  const isComparing = view === "compare" && hasMultipleScenarios && !isEditing && Boolean(baselineEntry);

  // We resolve the id against scenarios. A scenario with no outcomes has no entry, so entries[0] is the wrong write target.
  const activeScenario = useMemo(() => scenarios.find((scenario) => scenario.id === scenarioId) ?? entries[0]?.scenario, [scenarios, scenarioId, entries]);
  const activeEntry = useMemo(() => entries.find((entry) => entry.scenario.id === activeScenario?.id), [entries, activeScenario]);

  const cycle = useMemo(() => activeScenario?.years ?? [], [activeScenario]);
  const coverCropCostByYearId = useMemo(() => buildCoverCropCostByYearId(cycle, activeEntry?.data.rows ?? []), [cycle, activeEntry]);
  const resolveFallback = useMemo(
    () => buildFieldFallbackResolver(cropDefaultsByCropId, coverCropCostByYearId, coverCropDefaults),
    [cropDefaultsByCropId, coverCropCostByYearId, coverCropDefaults]
  );

  // Edits write straight through, so the years as they stood when editing began are what Discard restores.
  const [snapshot, setSnapshot] = useState<{ scenarioId: string; years: CropYear[] } | null>(null);
  // What runs once the farmer agrees to discard: a scenario switch or leaving the tab.
  const [pendingLeave, setPendingLeave] = useState<{ proceed: (isUpToDate: boolean) => void } | null>(null);
  const activeScenarioId = activeScenario?.id;

  // Adjusting state during render: the snapshot is retaken only when the edited scenario changes, never to follow edits.
  const snapshotKey = isEditing ? activeScenarioId ?? null : null;
  if ((snapshot?.scenarioId ?? null) !== snapshotKey) {
    setSnapshot(snapshotKey ? { scenarioId: snapshotKey, years: cycle } : null);
  }

  const hasUnsavedEdits = snapshot !== null && haveEditableFieldsChanged(snapshot.years, cycle);

  const handleSelectScenario = (scenarioId: string) => {
    // Re-picking the scenario already on screen goes nowhere, so it must not raise the discard prompt.
    if (scenarioId === activeScenarioId) return;
    if (hasUnsavedEdits) {
      setPendingLeave({ proceed: () => onSelectScenario(scenarioId) });
      return;
    }
    onSelectScenario(scenarioId);
  };

  useImperativeHandle<DetailedCalcsLeaveGuard, DetailedCalcsLeaveGuard>(
    leaveGuardRef,
    () => (proceed) => {
      if (!hasUnsavedEdits) {
        proceed();
        return;
      }
      setPendingLeave({ proceed: (isUpToDate) => proceed({ skipRecalculate: isUpToDate }) });
    },
    [hasUnsavedEdits]
  );

  const handleConfirmDiscard = () => {
    const isUpToDate = snapshot ? onDiscardEdits?.(snapshot.scenarioId, snapshot.years) ?? false : false;
    pendingLeave?.proceed(isUpToDate);
    setPendingLeave(null);
  };

  // No discard guard needed: editing already forces Details, so Compare never holds unsaved edits.
  const handleShowDetail = (scenarioId: string) => {
    onSelectScenario(scenarioId);
    onViewChange?.("detail");
  };

  // Compare draws every scenario with outcomes, so the selected one having none must not empty it.
  const hasContent = isEditing ? cycle.length > 0 : isComparing || (activeEntry?.data.rows.length ?? 0) > 0;
  const yearCountEntry = isComparing ? baselineEntry : activeEntry;

  if (!activeScenario || !hasContent) {
    // A run in flight is not an empty result. We show a loader instead of a recalculate button.
    if (isRecalculating) {
      return (
        <Flex justify="center" className="detailed-calculations-empty" data-testid={`${dataTestId}-calculating`}>
          <SoilSightLoader size="m" dataTestId={`${dataTestId}-calculating-spin`} />
        </Flex>
      );
    }

    return (
      <Flex vertical align="center" gap="small" className="detailed-calculations-empty" data-testid={`${dataTestId}-empty`}>
        <Typography variant="paragraph-m" color="grey-600">
          {t(`${PREFIX}${needsRotationFix ? "incompleteRotation" : "empty"}`)}
        </Typography>
        {needsRotationFix && onFixRotation ? (
          <Button type="primary" size="middle" onClick={onFixRotation} dataTestId={`${dataTestId}-empty-fix`}>
            {t(`${PREFIX}completeRotation`)}
          </Button>
        ) : null}
        {!needsRotationFix && onRecalculate ? (
          <Button
            type="primary"
            size="middle"
            icon={<Icon iconName="tabler/refresh" size="s" color="white" />}
            iconLeft
            onClick={onRecalculate}
            dataTestId={`${dataTestId}-empty-recalculate`}
          >
            {t("outcomeComparison.updateForecast")}
          </Button>
        ) : null}
      </Flex>
    );
  }

  const cropLabelFor = buildCropLabelResolver(mainCropLabelLookup);

  return (
    <div className="detailed-calculations-body detailed-calculations-body--inline" data-testid={dataTestId}>
      <div className="detailed-calculations-meta">
        {yearCountEntry ? <span className="detailed-calculations-badge">{t(`${PREFIX}yearCount`, { count: yearCountEntry.data.rows.length })}</span> : null}
        <span className="detailed-calculations-badge detailed-calculations-badge--muted">{t(`${PREFIX}unitNote`, { unit: `${currencySymbol}/ha` })}</span>
        <div className="detailed-calculations-header-actions" {...tourTargetProps("edit-values")}>
          {/* Hidden while editing: Compare has no inputs, and leaving would drop the edits. */}
          {hasMultipleScenarios && onViewChange && !isEditing ? (
            <div className="detailed-calculations-mode-toggle">
              <KlimRadioGroup
                value={isComparing ? "compare" : "detail"}
                onChange={(e) => onViewChange(e.target.value as DetailedCalcsView)}
                options={[
                  { value: "compare", label: t(`${PREFIX}modeCompare`) },
                  { value: "detail", label: t(`${PREFIX}modeDetails`) },
                ]}
                optionType="button"
                buttonStyle="solid"
                type="tertiary"
                size="middle"
                dataTestId={`${dataTestId}-mode`}
              />
            </div>
          ) : null}
          {isComparing ? null : isEditing ? (
            <Button type="secondary" size="middle" onClick={() => onEditingChange(false)} dataTestId={`${dataTestId}-done`}>
              {t(`${PREFIX}done`)}
            </Button>
          ) : (
            <Button
              type="secondary"
              size="middle"
              icon={<Icon iconName="tabler/pencil" size="s" color={CHART_COLORS["green-400"]} />}
              iconLeft
              disabled={isRecalculating}
              onClick={() => onEditingChange(true)}
              dataTestId={`${dataTestId}-edit`}
            >
              {t(`${PREFIX}editValues`)}
            </Button>
          )}
        </div>
      </div>

      {hasMultipleScenarios && !isComparing ? (
        <ScenarioPills
          scenarios={entries.map((entry) => entry.scenario)}
          colorSource={scenarios}
          isSelected={(scenario) => scenario.id === activeScenario.id}
          onSelect={handleSelectScenario}
          dataTestId={dataTestId}
        />
      ) : null}

      <div {...tourTargetProps("calculations-table")}>
        <StaleOverlay
          active={!isEditing && (isRecalculating || isStale)}
          isRecalculating={isRecalculating}
          onRecalculate={onRecalculate}
          dataTestId={dataTestId}
        >
          {isComparing && baselineEntry ? (
            <CompareTable
              entries={entries}
              baselineEntry={baselineEntry}
              mainCropLabelLookup={mainCropLabelLookup}
              onShowDetail={handleShowDetail}
              scenarioColor={(id) => scenarioLineColor(scenarios, id)}
              scrollY={COMPARE_TABLE_SCROLL_Y}
              dataTestId={`${dataTestId}-compare-table`}
            />
          ) : isEditing ? (
            <EditTable
              cycle={cycle}
              valueFor={(year, field) => effectiveFieldValue(year, field, resolveFallback)}
              onChange={(yearId, field, value) => onFieldChange(activeScenario.id, yearId, field, value)}
              cropLabelFor={cropLabelFor}
              dataTestId={`${dataTestId}-edit-table`}
            />
          ) : (
            activeEntry && (
              <DetailBreakdownTable
                data={activeEntry.data}
                mainCropLabelLookup={mainCropLabelLookup}
                otherExpanded={otherExpanded}
                onToggleOther={setOtherExpanded}
                dataTestId={`${dataTestId}-detail-table`}
              />
            )
          )}
        </StaleOverlay>
      </div>

      <DiscardChangesModal open={pendingLeave !== null} onKeepEditing={() => setPendingLeave(null)} onDiscard={handleConfirmDiscard} dataTestId={dataTestId} />
    </div>
  );
};

export default DetailedCalculationsTab;
